/**
 * Count-up formatting helpers for the Segment Analysis data presentation.
 *
 * The module renders pre-formatted display strings such as "3,120", "6.0%",
 * "260 MEUR", "5.4 / 10" and "A 5.4 - F 5.5". To animate the numeric parts
 * "counting up" while preserving every bit of non-numeric chrome (suffixes,
 * separators, decimals, prefixes), we:
 *
 *   1. Tokenize the target string into its numeric tokens, recording each
 *      token's value, decimal-place count and whether it used thousands
 *      grouping (so "3,120" keeps its comma and "6.0%" keeps its single
 *      decimal mid-animation).
 *   2. Interpolate every numeric token from a start frame towards the target
 *      and re-render it with the captured formatting.
 *
 * Strings with no clean numeric token (e.g. "Moderate") are returned
 * unchanged — the animation falls back to instant for them.
 *
 * This file is pure (no React, no DOM) so it can be unit-tested directly.
 */

/** A numeric token discovered inside a display string. */
export type NumberToken = {
  /** The parsed numeric value. */
  value: number
  /** Number of fractional digits to preserve (e.g. "6.0" -> 1). */
  decimals: number
  /** Whether the original token used thousands grouping ("3,120"). */
  grouped: boolean
  /** Index in the source string where the raw token starts. */
  start: number
  /** Length of the raw matched token. */
  length: number
}

// Matches an optional sign, grouped or plain integer part, and optional
// decimal part. Grouping is recognised as comma-separated groups of three.
const NUMBER_RE = /-?(?:\d{1,3}(?:,\d{3})+|\d+)(?:\.\d+)?/g

/** Clamp a number into the [0, 1] range. */
function clamp01(t: number): number {
  if (t <= 0) return 0
  if (t >= 1) return 1
  return t
}

/** Parse the numeric tokens out of a display string. */
export function tokenizeNumbers(input: string): NumberToken[] {
  const tokens: NumberToken[] = []
  for (const match of input.matchAll(NUMBER_RE)) {
    const raw = match[0]
    const index = match.index
    const grouped = raw.includes(",")
    const plain = raw.replace(/,/g, "")
    const value = Number(plain)
    if (!Number.isFinite(value)) continue
    const dotIndex = plain.indexOf(".")
    const decimals = dotIndex === -1 ? 0 : plain.length - dotIndex - 1
    tokens.push({ value, decimals, grouped, start: index, length: raw.length })
  }
  return tokens
}

/** Format a single numeric value to match a captured token's shape. */
export function formatLikeToken(
  value: number,
  token: Pick<NumberToken, "decimals" | "grouped">
): string {
  return value.toLocaleString("en-US", {
    useGrouping: token.grouped,
    minimumFractionDigits: token.decimals,
    maximumFractionDigits: token.decimals,
  })
}

/**
 * Render an intermediate frame of the count-up.
 *
 * `progress` in [0, 1] interpolates every numeric token in `target` from its
 * counterpart in `from` (matched positionally) up to the target value, while
 * leaving all non-numeric characters untouched. When the target has no numeric
 * tokens, the target string is returned verbatim.
 */
export function interpolateNumbers(target: string, from: number[], progress: number): string {
  const tokens = tokenizeNumbers(target)
  if (tokens.length === 0) return target

  const clamped = clamp01(progress)
  let result = ""
  let cursor = 0
  tokens.forEach((token, i) => {
    result += target.slice(cursor, token.start)
    const startValue = from[i] ?? 0
    const current = startValue + (token.value - startValue) * clamped
    result += formatLikeToken(current, token)
    cursor = token.start + token.length
  })
  result += target.slice(cursor)
  return result
}

/** Cubic ease-out — matches the project's KPI counter spec (DESIGN.md §6). */
export function easeOutCubic(t: number): number {
  const clamped = clamp01(t)
  return 1 - Math.pow(1 - clamped, 3)
}

/** Extract just the numeric values of a string (the count-up "from"/"to" basis). */
export function numericValues(input: string): number[] {
  return tokenizeNumbers(input).map((token) => token.value)
}

/** Whether a display string carries any numeric token worth animating. */
export function hasAnimatableNumber(input: string): boolean {
  return tokenizeNumbers(input).length > 0
}
